/**
 * Force-directed layout for the KB graph.
 *
 * Pure maths: no DOM, no canvas, no fetching. Given nodes and edges it returns
 * coordinates, which makes the part most likely to be wrong the part that is
 * cheapest to test.
 *
 * ── Why hand-rolled ─────────────────────────────────────────────────────
 * The SPA has no graph library and deliberately no framework (see DESIGN.md).
 * Adding d3-force for one view would pull in a dependency larger than this
 * file for behaviour we need a small, understood slice of.
 *
 * ── Why a spatial grid ──────────────────────────────────────────────────
 * Every node repels every other, which is O(n²): at the live KB's 3,427 nodes
 * that is 11.7 million pair calculations per tick, and a few hundred ticks of that locks the
 * main thread for seconds. Repulsion falls off with distance, so a node's
 * effect on one across the canvas is negligible — binning into cells and
 * repelling only within a 3×3 neighbourhood turns the cost linear in practice
 * while changing the result imperceptibly. This is Barnes-Hut's insight without
 * Barnes-Hut's tree.
 *
 * ── Why it settles rather than animates ─────────────────────────────────
 * A perpetually simmering graph is hostile to reading structure: things move
 * while you point at them. It runs a fixed number of ticks with a cooling
 * temperature, then stops. Deterministic too — seeded from node ids, so the
 * same KB lays out the same way on every visit.
 */

import { haloIsolated } from './kb_graph_halo.js'

export type LayoutNode = {
  id: string
  x: number
  y: number
  /** True when the node has no edges and was parked rather than simulated. */
  isolated: boolean
}
export type LayoutInput = {
  nodes: readonly { id: string }[]
  edges: readonly { from: string; to: string; weight: number }[]
}

/** Deterministic [0,1) from a string — a seeded start, so layouts are stable. */
export function hashUnit(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.codePointAt(i) ?? 0
    h = Math.imul(h, 16777619)
  }
  // `>>> 0` reinterprets the 32 bits as unsigned. Math.imul leaves h SIGNED, so
  // the lint suggestion of Math.trunc here is not equivalent — it would pass a
  // negative h through, the modulo would come back negative, and hashUnit would
  // start returning values outside [0,1).
  // The directive is oxlint's, not eslint's: eslint does not load the unicorn
  // plugin, and an eslint-disable naming a rule it cannot resolve is an ERROR.
  // oxlint-disable-next-line unicorn/prefer-math-trunc
  return ((h >>> 0) % 100000) / 100000
}

export type LayoutOptions = {
  width?: number
  height?: number
  ticks?: number
  /** Repulsion strength. */
  charge?: number
  /** Spring length for a connected pair. */
  linkDistance?: number
}

/**
 * Lay the graph out and return final positions.
 *
 * Runs to completion synchronously. At 3,427 nodes and 400 ticks this is a few
 * hundred milliseconds — acceptable once, on entering the view, with a spinner
 * up. Callers wanting to stay responsive should hand it to a worker; that is
 * deliberately not this function's problem.
 */
export function layoutGraph(input: LayoutInput, opts: LayoutOptions = {}): LayoutNode[] {
  // Isolated nodes are separated out BEFORE any physics runs.
  //
  // Two reasons, and the second is the important one:
  //
  //  - Room. In the live KB 912 of 3,427 entries have no edge. Left in the
  //    simulation they pack into a uniform disc under the centre pull and
  //    squeeze the 2,515 connected ones into an unreadable blob in the middle —
  //    the connected structure is precisely what the view exists to show.
  //
  //  - Honesty. A force layout says "near means related". A node with no edges
  //    has NO position information, so wherever the simulation puts it is an
  //    artefact of repulsion — and next to a cluster it invites a reader to
  //    infer a relationship the data does not contain.
  //
  // So the connected component is simulated across the whole canvas, and the
  // isolated entries are scattered in a ring AROUND it: visible, countable, and
  // clearly not claiming anything. The ring sits entirely outside the graph's
  // radius, which is what keeps it honest — a halo reads as "unattached", where
  // a dot resting against a cluster would read as "belongs to it".
  const degree = new Map<string, number>()
  for (const e of input.edges) {
    if (e.from === e.to) continue
    degree.set(e.from, (degree.get(e.from) ?? 0) + 1)
    degree.set(e.to, (degree.get(e.to) ?? 0) + 1)
  }
  const known = new Set(input.nodes.map((n) => n.id))
  const connected = input.nodes.filter((n) => (degree.get(n.id) ?? 0) > 0)
  const isolated = input.nodes.filter((n) => (degree.get(n.id) ?? 0) === 0)

  if (isolated.length > 0 && connected.length > 0) {
    const placed = simulate(
      { nodes: connected, edges: input.edges.filter((e) => known.has(e.from) && known.has(e.to)) },
      opts,
    )
    return [...placed, ...haloIsolated(isolated, placed)]
  }
  if (connected.length === 0) return haloIsolated(input.nodes, [])
  return simulate(input, opts)
}

function simulate(input: LayoutInput, opts: LayoutOptions = {}): LayoutNode[] {
  const width = opts.width ?? 1600
  const height = opts.height ?? 1200
  const ticks = opts.ticks ?? 400
  const charge = opts.charge ?? 900
  const linkDistance = opts.linkDistance ?? 60

  const n = input.nodes.length
  if (n === 0) return []

  // Seeded on a circle rather than at random: a circle has no initial clumps
  // for the simulation to spend its early ticks undoing, and seeding from the
  // id keeps the result reproducible.
  const xs = new Float64Array(n)
  const ys = new Float64Array(n)
  const vx = new Float64Array(n)
  const vy = new Float64Array(n)
  const index = new Map<string, number>()

  input.nodes.forEach((node, i) => {
    index.set(node.id, i)
    const a = hashUnit(node.id) * Math.PI * 2
    const r = (0.25 + 0.75 * hashUnit(`${node.id}:r`)) * Math.min(width, height) * 0.45
    xs[i] = width / 2 + Math.cos(a) * r
    ys[i] = height / 2 + Math.sin(a) * r
  })

  // Edges resolved to indices once, so the tick loop never touches the map.
  const ea: number[] = []
  const eb: number[] = []
  for (const e of input.edges) {
    const a = index.get(e.from)
    const b = index.get(e.to)
    if (a !== undefined && b !== undefined && a !== b) {
      ea.push(a)
      eb.push(b)
    }
  }

  // Cell size tracks the distance beyond which repulsion stops mattering.
  const cell = Math.max(linkDistance * 1.5, 40)
  const cols = Math.max(1, Math.ceil(width / cell))
  const rows = Math.max(1, Math.ceil(height / cell))
  const buckets: number[][] = Array.from({ length: cols * rows }, () => [])

  for (let t = 0; t < ticks; t++) {
    // Temperature falls linearly: big early moves to find the gross shape, then
    // small ones to settle. Without cooling the graph jitters forever.
    const temp = 1 - t / ticks

    for (const b of buckets) b.length = 0
    for (let i = 0; i < n; i++) {
      const cx = Math.min(cols - 1, Math.max(0, Math.floor((xs[i] ?? 0) / cell)))
      const cy = Math.min(rows - 1, Math.max(0, Math.floor((ys[i] ?? 0) / cell)))
      buckets[cy * cols + cx]?.push(i)
    }

    // Repulsion, 3x3 neighbourhood only.
    for (let cy = 0; cy < rows; cy++) {
      for (let cx = 0; cx < cols; cx++) {
        const here = buckets[cy * cols + cx]
        if (!here || here.length === 0) continue
        for (let ny = Math.max(0, cy - 1); ny <= Math.min(rows - 1, cy + 1); ny++) {
          for (let nx = Math.max(0, cx - 1); nx <= Math.min(cols - 1, cx + 1); nx++) {
            const there = buckets[ny * cols + nx] ?? []
            for (const i of here) {
              const xi = xs[i] ?? 0
              const yi = ys[i] ?? 0
              for (const j of there) {
                if (i >= j) continue
                let dx = xi - (xs[j] ?? 0)
                let dy = yi - (ys[j] ?? 0)
                let d2 = dx * dx + dy * dy
                if (d2 === 0) {
                  // Exactly coincident nodes have no direction to separate in.
                  // Nudge deterministically rather than with Math.random, or the
                  // layout stops being reproducible.
                  dx = (hashUnit(`${i}:${j}`) - 0.5) * 0.01
                  dy = (hashUnit(`${j}:${i}`) - 0.5) * 0.01
                  d2 = dx * dx + dy * dy || 1e-6
                }
                const f = charge / d2
                const d = Math.sqrt(d2)
                const ux = (dx / d) * f
                const uy = (dy / d) * f
                vx[i] = (vx[i] ?? 0) + ux
                vy[i] = (vy[i] ?? 0) + uy
                vx[j] = (vx[j] ?? 0) - ux
                vy[j] = (vy[j] ?? 0) - uy
              }
            }
          }
        }
      }
    }

    // Attraction along edges — a spring pulling connected nodes to linkDistance.
    for (let k = 0; k < ea.length; k++) {
      const i = ea[k] ?? 0
      const j = eb[k] ?? 0
      const dx = (xs[j] ?? 0) - (xs[i] ?? 0)
      const dy = (ys[j] ?? 0) - (ys[i] ?? 0)
      const d = Math.sqrt(dx * dx + dy * dy) || 1e-6
      const f = (d - linkDistance) * 0.02
      const ux = (dx / d) * f
      const uy = (dy / d) * f
      vx[i] = (vx[i] ?? 0) + ux
      vy[i] = (vy[i] ?? 0) + uy
      vx[j] = (vx[j] ?? 0) - ux
      vy[j] = (vy[j] ?? 0) - uy
    }

    // Weak pull to centre, so disconnected nodes drift into view instead of
    // being flung to the edges by repulsion alone and never seen again.
    const step = 12 * temp
    for (let i = 0; i < n; i++) {
      const xi = xs[i] ?? 0
      const yi = ys[i] ?? 0
      // Damping plus temperature; the clamp stops a node in a dense cluster
      // acquiring enough velocity to shoot off screen in one tick.
      const nvx = ((vx[i] ?? 0) + (width / 2 - xi) * 0.002) * 0.85
      const nvy = ((vy[i] ?? 0) + (height / 2 - yi) * 0.002) * 0.85
      vx[i] = nvx
      vy[i] = nvy
      xs[i] = xi + Math.max(-step, Math.min(step, nvx))
      ys[i] = yi + Math.max(-step, Math.min(step, nvy))
    }
  }

  return input.nodes.map((node, i) => ({
    id: node.id,
    x: xs[i] ?? 0,
    y: ys[i] ?? 0,
    isolated: false,
  }))
}
